/** Invoice statuses that count as approved for fulfillment retry. */
const APPROVED_STATUS_IDS = ["sent", "overdue", "paid"];

/** Issue codes Process Fulfillment can retry, plus the Xero short-stock warning. */
const FULFILLMENT_ISSUE_CODES = [
  "INSUFFICIENT_STOCK",
  "FULFILLMENT_FAILED",
  "MISSING_PACKAGING_TYPE",
  "MISSING_PACK_LOCATION",
  "XERO_APPROVED_SHORT_STOCK",
];

/**
 * Return the status id on an invoice or a line with a nested invoice.
 *
 * @param {Object} [record]
 * @returns {string|undefined}
 */
export const recordStatusId = record =>
  record?.status?.id || record?.status || record?.invoice?.status?.id || record?.invoice?.status;

/**
 * Return whether a tag or issue type is retryable.
 *
 * @param {string} [type]
 * @returns {boolean}
 */
const isRetryableIssueType = type => type === "error" || type === "warning";

/**
 * Return whether the invoice or line still has a fulfillment issue to retry.
 *
 * List rows expose `has_open_issues` / type-only tags. Lines expose `issues.items`,
 * which may be type-only after retrieve. Processed pills are not retryable.
 *
 * @param {Object} [record]
 * @returns {boolean}
 */
export const hasFulfillmentIssueNeedingProcessing = record => {
  if (record?.has_open_issues) return true;
  const tags = record?.open_issue_tags;
  if (Array.isArray(tags) && tags.some(tag => isRetryableIssueType(tag?.type))) {
    return true;
  }
  const items = record?.issues?.items;
  if (!Array.isArray(items) || !items.length) return false;
  return items.some(item => {
    if (item?.code) return FULFILLMENT_ISSUE_CODES.includes(item.code);
    return isRetryableIssueType(item?.type);
  });
};

/**
 * Exclusive enablement for Approve vs Process Fulfillment.
 *
 * Approve is enabled only when every selected record is awaiting approval
 * and none are managed in Xero. Process Fulfillment stays available on
 * Xero-linked invoices so short-stock after Xero approve can still be retried.
 * A mix of those two cases, or any other selection, disables both.
 *
 * @param {Object[]} [records]
 * @returns {{canApprove: boolean, canFulfill: boolean}}
 */
export const saleInvoiceApproveFulfillmentVisibility = records => {
  if (!records?.length) {
    return { canApprove: false, canFulfill: false };
  }

  const anyManagedInXero = records.some(record => record?.managed_in_xero);
  const allAwaitingApproval = records.every(
    record => recordStatusId(record) === "awaiting_approval",
  );
  const allApprovedNeedingFulfillment = records.every(
    record =>
      APPROVED_STATUS_IDS.includes(recordStatusId(record)) &&
      hasFulfillmentIssueNeedingProcessing(record),
  );

  if (allAwaitingApproval) {
    return { canApprove: !anyManagedInXero, canFulfill: false };
  }
  if (allApprovedNeedingFulfillment) {
    return { canApprove: false, canFulfill: true };
  }
  return { canApprove: false, canFulfill: false };
};

/**
 * Extra operations that are hidden until the record qualifies.
 *
 * On the list, these are omitted until exactly one invoice is selected
 * and its status allows the action. On the detail page, the same status
 * rules apply to the open invoice.
 *
 * @param {number} selectedCount
 * @param {string|undefined} statusId
 * @returns {{
 *   canSubmit: boolean,
 *   canMarkPaid: boolean,
 *   canRecordPayment: boolean,
 *   canVoid: boolean,
 *   canWriteOff: boolean,
 *   canDelete: boolean,
 *   canEdit: boolean
 * }}
 */
export const canCreditNote = statusId =>
  ["sent", "overdue", "paid"].includes(statusId);

export const saleInvoiceOpenAmountDue = record => {
  if (!record) return 0;
  if (record.amount_due != null && record.amount_due !== "") {
    return Number(record.amount_due);
  }
  if (record.managed_in_xero && record.xero_amount_due != null) {
    return Number(record.xero_amount_due);
  }
  const total = Number(record.total_amount) || 0;
  const paid = Number(record.amount_paid) || 0;
  return Math.max(total - paid, 0);
};

export const saleInvoiceExtraActionAbility = (
  selectedCount,
  statusId,
  { managedInXero = false, amountDue = null } = {},
) => {
  const hasOne = selectedCount === 1;
  const isDraft = statusId === "draft";
  const isAwaitingApproval = statusId === "awaiting_approval";
  const isSentOrOverdue = statusId === "sent" || statusId === "overdue";
  const canVoidStatus = ["draft", "awaiting_approval"].includes(statusId);
  const due =
    amountDue != null ? Number(amountDue) : isSentOrOverdue ? null : 0;
  const hasOpenBalance = due == null || due > 0;

  if (managedInXero) {
    return {
      canSubmit: false,
      canMarkPaid: false,
      canRecordPayment: false,
      canVoid: false,
      canWriteOff: false,
      canDelete: false,
      canEdit: false,
    };
  }

  return {
    canSubmit: hasOne && isDraft,
    canMarkPaid: hasOne && isSentOrOverdue && !hasOpenBalance,
    canRecordPayment: hasOne && isSentOrOverdue && hasOpenBalance,
    canVoid: hasOne && canVoidStatus,
    canWriteOff: hasOne && isAwaitingApproval,
    canDelete: hasOne && isDraft,
    canEdit: hasOne && isDraft,
  };
};
